// @ts-nocheck
import _ from 'lodash';
import axios from 'axios';
import { create } from 'zustand';
import { API_URL } from '../configs';
import { toastTypes } from '../enums';
import getRoot from './root';

const DEFAULT_LOADING = {
  getDomains: false,
  getTemplate: false,
  getTemplates: false,
  addTemplate: false,
  uploadTemplates: false,
  updateTemplate: false,
  deleteTemplate: false,
  sendTestEmail: false,
  saveEmailSettings: false,
  saveEmailLogic: false,
  addEmailDomain: false,
  getNSRecords: false,
  verifyRecords: false,
};

const useEmailManagementStore = create((set, get) => ({
  activeEmailDomainName: '',
  emailDomains: {},
  templates: [],
  relevantTemplates: [],
  loading: DEFAULT_LOADING,

  // Computed
  activeEmailDomain: () => get().emailDomains[get().activeEmailDomainName] || {},

  reset: () => set({
    activeEmailDomainName: '',
    emailDomains: {},
    templates: [],
    loading: DEFAULT_LOADING,
  }),

  getIdToken: () => getRoot().firebase.auth().currentUser.getIdToken(),

  setActiveEmailDomainName: (_e, { value: emailDomainName }) => {
    const { emailDomains } = get();
    if (emailDomains[emailDomainName]) {
      set({ activeEmailDomainName: emailDomainName });
      get().getTemplates();
      return true;
    }
    getRoot().failure('Invalid email domain!');
    return false;
  },

  getEmailDomains: async () => {
    set((s) => ({ loading: { ...s.loading, getDomains: true } }));
    try {
      const { data } = await axios.get(`${API_URL}/api/em/email-domains`);
      const emailDomains = data.emailDomains || {};
      let { activeEmailDomainName } = get();
      if (_.size(emailDomains) && !activeEmailDomainName) {
        [activeEmailDomainName] = Object.keys(emailDomains);
      }
      set({ emailDomains, activeEmailDomainName });
    } catch (err) {
      getRoot().failure('Error getting email Domains');
    }
    set((s) => ({ loading: { ...s.loading, getDomains: false } }));
    return get().emailDomains;
  },

  getTemplate: async (templateName) => {
    set((s) => ({ loading: { ...s.loading, getTemplate: true } }));
    let template;
    try {
      const { data } = await axios.get(`${API_URL}/api/em/email-template?name=${templateName}&emailDomain=${get().activeEmailDomainName}`);
      const templates = get().templates.slice();
      const idx = templates.findIndex((t) => t.name === templateName);
      template = { ...templates[idx], ...data.template.version };
      templates[idx] = template;
      set({ templates });
    } catch (err) {
      getRoot().failure('Error getting template HTML!');
    }
    set((s) => ({ loading: { ...s.loading, getTemplate: false } }));
    return template;
  },

  getTemplates: async (active = false, limit = 100) => {
    const { activeEmailDomainName } = get();
    if (!activeEmailDomainName) return;
    set((s) => ({ loading: { ...s.loading, getTemplates: true } }));
    try {
      const { data } = await axios.get(`${API_URL}/api/em/email-templates?active=${active}&limit=${limit}&emailDomain=${activeEmailDomainName}`);
      if (data.success) set({ templates: data.templates || [] });
    } catch (err) {
      set({ templates: [] });
      getRoot().failure('Failed to get templates');
    }
    set((s) => ({ loading: { ...s.loading, getTemplates: false } }));
  },

  addTemplate: async ({ name, template, description, tag, comment, active, versioned }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, addTemplate: true } }));
    try {
      await axios.post(`${API_URL}/api/em/email-template`, {
        name, template, description, tag, comment, active, versioned,
        emailDomain: get().activeEmailDomainName,
      });
      get().getTemplates();
      root.clearToast(toastTypes.addTemplate);
      root.success('Successfully added template!', toastTypes.addTemplate);
    } catch (err) {
      root.failure('Error uploading single template', toastTypes.addTemplate);
    }
    set((s) => ({ loading: { ...s.loading, addTemplate: false } }));
  },

  uploadTemplates: async (files) => {
    const root = getRoot();
    if (!files || files.length === 0) {
      root.failure('No files selected.');
      return;
    }
    set((s) => ({ loading: { ...s.loading, uploadTemplates: true } }));
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('templates', f));
      const { data } = await axios.post(`${API_URL}/api/em/email-templates`, formData, {
        headers: {
          Authorization: await get().getIdToken(),
          EmailDomain: get().activeEmailDomainName,
        },
      });
      const list = Array.isArray(data) ? data : [data];
      if (list.every((entry) => _.get(entry, 'success', true))) {
        root.success('Successfully uploaded template(s)!');
      } else {
        throw new Error('Failed to upload email template');
      }
      get().getTemplates();
    } catch (err) {
      root.failure('Error uploading email templates');
    }
    set((s) => ({ loading: { ...s.loading, uploadTemplates: false } }));
  },

  updateTemplate: async ({ name, template, comment, description, versionTag }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, updateTemplate: true } }));
    try {
      await axios.put(`${API_URL}/api/em/email-template`, {
        name, template, comment, description, active: true, versionTag,
        emailDomain: get().activeEmailDomainName,
      });
      const templates = get().templates.slice();
      const idx = templates.findIndex((t) => t.name === name);
      if (idx >= 0) templates[idx] = { ...templates[idx], template };
      set({ templates });
      root.success('Successfully updated template!', toastTypes.updateTemplate);
      root.clearToast(toastTypes.updateTemplate);
    } catch (err) {
      root.failure('Error updating template', toastTypes.updateTemplate);
    }
    set((s) => ({ loading: { ...s.loading, updateTemplate: false } }));
  },

  deleteTemplate: async (name, versionTag) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, deleteTemplate: true } }));
    try {
      await axios.delete(`${API_URL}/api/em/email-template`, {
        data: { name, versionTag, emailDomain: get().activeEmailDomainName },
      });
      get().getTemplates();
      root.success('Successfully deleted template', toastTypes.deleteTemplate);
      root.clearToast(toastTypes.deleteTemplate);
    } catch (err) {
      root.failure('Error deleting template.', toastTypes.deleteTemplate);
    }
    set((s) => ({ loading: { ...s.loading, deleteTemplate: false } }));
  },

  sendTestEmail: async ({ to, from, subject, name, formData }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, sendTestEmail: true } }));
    try {
      await axios.post(`${API_URL}/api/em/test-email`, {
        to, from, subject, templateName: name, formData,
        emailDomain: get().activeEmailDomainName,
      });
      root.success(`Successfully sent test e-mail to ${to}`, toastTypes.sendTestEmail);
      root.clearToast(toastTypes.sendTestEmail);
    } catch (err) {
      root.failure('Error sending test e-mail', toastTypes.sendTestEmail);
    }
    set((s) => ({ loading: { ...s.loading, sendTestEmail: false } }));
  },

  saveEmailSettings: async ({ url, domain, sender, logic }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, saveEmailSettings: true } }));
    try {
      if (!sender) {
        root.failure('Sender is required', toastTypes.saveEmailSettings);
        set((s) => ({ loading: { ...s.loading, saveEmailSettings: false } }));
        return;
      }
      if (!get().emailDomains[domain]) {
        root.failure('Invalid email domain', toastTypes.saveEmailSettings);
        set((s) => ({ loading: { ...s.loading, saveEmailSettings: false } }));
        return;
      }
      await axios.post(`${API_URL}/api/forms/recover-meta`, {
        url: url.href, emailDomain: domain, emailSender: sender,
      });
      root.forms.updateForm({
        url,
        diff: { emailLogic: { emailDomain: domain, emailSender: sender, logic } },
      });
      root.success('Successfully updated recover form settings', toastTypes.saveEmailSettings);
      root.clearToast('saveEmailSettings', toastTypes.saveEmailSettings);
    } catch (err) {
      root.failure('Failed to update recover form settings', toastTypes.saveEmailSettings);
    }
    set((s) => ({ loading: { ...s.loading, saveEmailSettings: false } }));
  },

  saveEmailLogic: async ({ url, logic, emailDomain, emailSender }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, saveEmailLogic: true } }));
    try {
      await axios.post(`${API_URL}/api/forms/recover-logic`, { url, logic, emailDomain, emailSender });
      root.forms.updateForm({ url, diff: { emailLogic: { logic }, emailDomain, emailSender } });
      root.success('Successfully updated recover logic', toastTypes.saveEmailLogic);
      root.clearToast(toastTypes.saveEmailLogic);
    } catch (err) {
      root.failure('Failed to save recover logic', toastTypes.saveEmailLogic);
    }
    set((s) => ({ loading: { ...s.loading, saveEmailLogic: false } }));
  },

  saveRecoverEmails: async ({ url, logic, emailDomain, emailSender }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, saveEmailLogic: true } }));
    if (logic.some((t) => !t.minuteDifference || !t.subject)) {
      root.failure('E-mails need a subject and hour difference!', toastTypes.saveEmailLogic);
    } else {
      try {
        await axios.post(`${API_URL}/api/forms/recover-logic`, { url, logic, emailDomain, emailSender });
        root.forms.updateForm({ url, diff: { emailLogic: { logic }, emailDomain, emailSender } });
        root.success('Successfully updated recover logic', toastTypes.saveEmailLogic);
        root.clearToast(toastTypes.saveEmailLogic);
      } catch (error) {
        root.failure('Failed to save recover logic', toastTypes.saveEmailLogic);
      }
    }
    set((s) => ({ loading: { ...s.loading, saveEmailLogic: false } }));
  },

  getNSRecords: async ({ emailDomain }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, getNSRecords: true } }));
    try {
      const { data } = await axios.get(`${API_URL}/api/records/retrieve-ns-records`);
      root.success('Successfully retrieved NS records', toastTypes.getNSRecords);
      root.clearToast(toastTypes.getNSRecords);
      return data.records;
    } catch (err) {
      root.failure(`Failed to get NS records for domain: ${emailDomain}`, toastTypes.getNSRecords);
    }
    set((s) => ({ loading: { ...s.loading, getNSRecords: false } }));
    return null;
  },

  addEmailDomain: async ({ emailDomain }) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, addEmailDomain: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/records/setup-dns-records`, { emailDomain });
      if (data.success) {
        await get().getEmailDomains();
        get().setActiveEmailDomainName(null, { value: emailDomain });
        root.success(`Successfully added email domain: ${emailDomain}!`, toastTypes.addEmailDomain);
        root.clearToast(toastTypes.addEmailDomain);
      } else {
        root.failure(`Failed to add domain: ${emailDomain}`, toastTypes.addEmailDomain);
      }
    } catch (err) {
      root.failure(`Failed to add domain: ${emailDomain}`, toastTypes.addEmailDomain);
    }
    set((s) => ({ loading: { ...s.loading, addEmailDomain: false } }));
  },

  verifyRecords: async ({ emailDomain = get().activeEmailDomainName } = {}) => {
    const root = getRoot();
    set((s) => ({ loading: { ...s.loading, verifyRecords: true } }));
    try {
      const { data } = await axios.post(`${API_URL}/api/records/verify-dns-records`, { emailDomain });
      if (data.success) {
        await get().getEmailDomains();
        root.success('Verified DNS records!', toastTypes.verifyRecords);
        root.clearToast(toastTypes.verifyRecords);
      } else {
        root.failure(`Failed to verify domain: ${emailDomain}`, toastTypes.verifyRecords);
      }
    } catch (err) {
      root.failure(`Failed to verify domain: ${emailDomain}`, toastTypes.verifyRecords);
    }
    set((s) => ({ loading: { ...s.loading, verifyRecords: false } }));
  },
}));

export default useEmailManagementStore;
